import { apiClient } from './apiClient';

// Full user object as returned by /api/users, including active plan
export type BackendPlan = {
    id: number;
    name: string;
    enabled: boolean;
    price: string;
    currency: string;
    billing_interval: number | null;
    features: string[];
    precedence: number;
    checkout_url?: string;
};

export type ActivePlan = {
    id: number;
    plan: BackendPlan;
    start_date: string;
    end_date: string | null;
    status: string;
};

/** Mentorship role surfaced on the user profile (v1). null = not yet
 * onboarded into the mentorship program. */
export type MentorshipRole = 'mentor' | 'mentee' | 'admin' | null;

export type ModuleKey =
    | 'concept'
    | 'design'
    | 'authorities'
    | 'contract'
    | 'tender'
    | 'construction';

/** Tiny mentor projection inlined on UserProfile when the mentee has an
 * assigned mentor. Mirrors backend ``MentorSummarySerializer``. */
export type MentorSummary = {
    id: number;
    first_name: string;
    last_name: string;
    email: string;
};

export type BackendUserWithPlan = {
    id: number;
    username: string;
    email: string;
    first_name: string;
    last_name: string;
    profile: {
        id: number;
        background: string;
        focus_areas: string;
        industry_knowledge: number | null;
        desire: string;
        anxieties: string;
        user: number;
        location?: string;
        experience?: string;
        // ── Mentorship v1 fields (additive; null/empty when the user has
        // not yet been onboarded into the mentorship program) ──
        role?: MentorshipRole;
        mentor_id?: number | null;
        mentor_summary?: MentorSummary | null;
        mentor_specialties?: ModuleKey[];
        mentor_capacity?: number;
        role_assigned_at?: string | null;
        role_assigned_by_id?: number | null;
    };
    active_plan: ActivePlan | null;
};

export async function fetchCurrentUserWithPlan(): Promise<BackendUserWithPlan | null> {
    try {
        // Adjust path if your backend exposes a different endpoint,
        // e.g. '/api/users/me/' instead of '/api/users/'.
        const response = await apiClient.get<BackendUserWithPlan>('/api/user/');
        return response.data ?? null;
    } catch (err) {
        console.warn('[userService] Failed to fetch current user with plan', err);
        return null;
    }
}

export async function fetchBillingPortalUrl(): Promise<string | null> {
    try {
        const response = await apiClient.get<{ url: string }>('/api/user/billing-portal/');
        return response.data.url;
    } catch (err) {
        console.error('[userService] Failed to fetch billing portal URL', err);
        return null;
    }
}

// ── Mentorship role helpers ────────────────────────────────────────────────
// SSOT for role checks across the mentorship UI. Every component that needs
// to branch on role MUST go through these helpers — no inline
// `user?.profile?.role === 'mentor'` checks in components.

/** The user's current mentorship role, or `null` if not yet onboarded. */
export function getMentorshipRole(user: BackendUserWithPlan | null | undefined): MentorshipRole {
    return user?.profile?.role ?? null;
}

export function isMentor(user: BackendUserWithPlan | null | undefined): boolean {
    return getMentorshipRole(user) === 'mentor';
}

export function isMentee(user: BackendUserWithPlan | null | undefined): boolean {
    return getMentorshipRole(user) === 'mentee';
}

export function isAdmin(user: BackendUserWithPlan | null | undefined): boolean {
    return getMentorshipRole(user) === 'admin';
}

/** Any mentorship role (mentor, mentee, or admin). Useful for gating
 * the Coaching sub-tab swap away from the legacy course list. */
export function hasMentorshipRole(user: BackendUserWithPlan | null | undefined): boolean {
    const role = getMentorshipRole(user);
    return role === 'mentor' || role === 'mentee' || role === 'admin';
}

/** Mentor or admin — the "can curate" predicate used by many surfaces
 * (Risk add/edit, Insight curation, ClientProfile CRUD). */
export function canCurate(user: BackendUserWithPlan | null | undefined): boolean {
    return isMentor(user) || isAdmin(user);
}

